import React, { FC, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_PRODUCTS, GET_USER } from "../../../graphql/queries";
import {
    Product,
    ProductsAdminData,
    UpdateUserData,
    UpdateUserVariables,
    User
} from "../../../models/types";
import {
    Button,
    Col,
    Form,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import { UPDATE_USER_ACCESS_BATCH } from "../../../graphql/mutations";
interface Props {
    user: User;
    updateProducts: (productId: number, handleProjects: boolean) => void;
    onFilteredProductsChange?: (products: Product[] | undefined) => void;
}

export const SelectableProducts: FC<Props> = ({
    user,
    updateProducts,
    onFilteredProductsChange
}) => {
    const [handleRelatedProjects, updateRelatedProjects] = useState(false);
    const [search, setSearch] = useState("");
    const { data: { allProducts } = {} } = useQuery<ProductsAdminData>(
        GET_PRODUCTS,
        {
            variables: {
                organizationId: Number(user.organization?.id)
            }
        }
    );

    const filteredProducts = allProducts?.filter((product: Product) => {
        const term = search.trim().toLowerCase();
        return (
            product.name.toLowerCase().includes(term) ||
            product.organization?.name.toLowerCase().includes(term)
        );
    });

    useEffect(() => {
        onFilteredProductsChange?.(
            search.trim() ? filteredProducts : undefined
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, allProducts]);
    const [selectAll] = useMutation<UpdateUserData, UpdateUserVariables>(
        UPDATE_USER_ACCESS_BATCH
    );
    const selectAllButton = () => {
        if (user.organization) {
            selectAll({
                variables: {
                    userId: user.id,
                    organizationId: user.organization.id
                },
                refetchQueries: [
                    {
                        query: GET_USER,
                        variables: {
                            userId: user.id
                        }
                    }
                ]
            }).catch(error => {
                console.log(error);
            });
        }
    };
    const selectedProduct = (product: Product): boolean => {
        if (!user.products) {
            return false;
        }
        if (
            user.products.some(
                (userProduct: Product) => userProduct.id === product.id
            )
        ) {
            return true;
        }
        return false;
    };

    const alternatives = filteredProducts?.map(
        (product: Product, index: number) => (
            <li
                key={index}
                className={cx(
                    "list-group-item  mouseHover",
                    selectedProduct(product)
                        ? "list-group-item-primary"
                        : "list-group-item-secondary"
                )}
                onClick={() =>
                    updateProducts(Number(product.id), handleRelatedProjects)
                }
            >
                {product.id}: {product.name} ({product.organization?.name})
            </li>
        )
    );

    const [showList, setShowList] = useState(true);
    const listPopulated = allProducts && allProducts?.length > 0;

    return (
        <>
            <Row className="m-0 p-0">
                <Col className=" d-flex align-items-center mb-2">
                    <h4
                        className="mouseHover"
                        onClick={() => setShowList(!showList)}
                    >
                        Products
                        {listPopulated && (
                            <FontAwesomeIcon
                                icon={showList ? faChevronUp : faChevronDown}
                                style={{
                                    color: "gray",
                                    marginLeft: "6px",
                                    fontSize: "16px"
                                }}
                            />
                        )}
                    </h4>
                    {listPopulated && (
                        <div className="float-end ms-auto">
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={
                                    <Tooltip id="tooltip-update-user-projects">
                                        {handleRelatedProjects
                                            ? "Turn off to select Projects separately"
                                            : "Turn on to update related Projects when selecting Products"}
                                    </Tooltip>
                                }
                            >
                                <Button
                                    className="ms-2"
                                    variant={
                                        handleRelatedProjects
                                            ? "success"
                                            : "secondary"
                                    }
                                    onClick={() =>
                                        updateRelatedProjects(
                                            !handleRelatedProjects
                                        )
                                    }
                                >
                                    {handleRelatedProjects
                                        ? "Updating projects also"
                                        : "Update projects?"}
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={
                                    <Tooltip id="tooltip-disabled">
                                        {search.trim()
                                            ? "Clear search to select all Products and Projects"
                                            : "Select all Products and Projects"}
                                    </Tooltip>
                                }
                            >
                                <span className="ms-2 d-inline-block">
                                    <Button
                                        disabled={!!search.trim()}
                                        onClick={selectAllButton}
                                        style={
                                            search.trim()
                                                ? { pointerEvents: "none" }
                                                : undefined
                                        }
                                    >
                                        Select All
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        </div>
                    )}
                </Col>
            </Row>
            {showList && listPopulated && (
                <Row className="m-0 p-0">
                    <Col className="mb-2">
                        <Form.Control
                            type="text"
                            placeholder="Search products (will also filter projects) ..."
                            value={search}
                            onChange={(event: any) =>
                                setSearch(event.target.value)
                            }
                        />
                    </Col>
                </Row>
            )}
            {showList &&
                (alternatives && alternatives.length > 0 ? (
                    <ul className="list-group" id="selectable-products-list">
                        {alternatives}
                    </ul>
                ) : (
                    <div className="text-inverse-50 xs-font p-3 ms-2">
                        No results.
                    </div>
                ))}
        </>
    );
};
